/** Returns the value when it is a string, or the fallback. */
export function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

/** Returns the value when it is a string with text, or null. */
export function readNullableString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

/** Returns the value when it is a finite number, or the fallback. */
export function readNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Returns the strings of a list, or an empty list. */
export function readStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/** Returns the value when it is an object, or an empty object. */
export function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/** Returns each item of a list as an object, or an empty list. */
export function readRecords(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(readRecord) : [];
}

/** Returns the value when it is one of the values, or the fallback. */
export function readOneOf<T extends string>(values: readonly T[], value: unknown, fallback: T): T {
  return values.find((candidate) => candidate === value) ?? fallback;
}

/** Returns the value when it is one of the values, or null. */
export function readNullableOneOf<T extends string>(values: readonly T[], value: unknown): T | null {
  return values.find((candidate) => candidate === value) ?? null;
}

/** Returns the finite numbers of a list, or an empty list. */
export function readNumberList(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is number => typeof item === 'number' && Number.isFinite(item));
}

/** Returns each row of an `{ items }` response that parses, with the `id` of the item as its id. */
export function parseItems<T>(response: unknown, parse: (id: string, raw: Record<string, unknown>) => T | null): T[] {
  return readRecords(readRecord(response).items)
    .map(({ id, ...raw }) => parse(readString(id), raw))
    .filter((row): row is T => row !== null);
}

/** Returns the row in a write response, or null when the field `idField` has no id. */
export function parseWriteResponse<T>(
  response: unknown,
  idField: string,
  parse: (id: string, raw: Record<string, unknown>) => T | null,
): T | null {
  const raw = readRecord(response);
  const id = readString(raw[idField]);
  return id ? parse(id, raw) : null;
}

/** Returns the parsed value, or throws when the parser returned null. */
export function requireParsed<T>(value: T | null, name: string): T {
  if (value === null) throw new Error(`The response has no valid ${name}.`);
  return value;
}
